import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Drawer from '@mui/material/Drawer';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import { Close as CloseIcon } from '@mui/icons-material';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useAdminEmailSectionTaxonomyControllerListTags } from 'src/apis/backend/generated/admin-email-section-taxonomy/admin-email-section-taxonomy';
import {
  type EmailDocumentSectionResponseDtoCategory,
  EmailDocumentSectionResponseDtoCategory as CategoryEnum,
} from 'src/apis/backend/generated/models';
import {
  useEmailDocumentSectionsControllerUpdate,
  useEmailDocumentSectionsControllerGetOne,
} from 'src/apis/backend/generated/email-document-sections/email-document-sections';

const CATEGORIES: EmailDocumentSectionResponseDtoCategory[] = [
  CategoryEnum.header,
  CategoryEnum.hero,
  CategoryEnum.banner,
  CategoryEnum.content,
  CategoryEnum.cta,
  CategoryEnum.product,
  CategoryEnum.image,
  CategoryEnum.footer,
];

interface Props {
  open: boolean;
  onClose: () => void;
  orgId: string;
  clientId: string;
  sectionId: string;
}

// Section settings drawer for raw-HTML rows. Mirrors the metadata fields
// from the structured editor's drawer (name / description / category /
// tags / active) but skips slot configuration since raw HTML has no
// templated placeholders. Edits PATCH the row via the same mutation the
// chat editor's manual save uses.
export function SectionSettingsDrawer({
  open,
  onClose,
  orgId,
  clientId,
  sectionId,
}: Props) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const sectionQuery = useEmailDocumentSectionsControllerGetOne(
    orgId,
    clientId,
    sectionId,
    { query: { enabled: open } },
  );
  const tagsQuery = useAdminEmailSectionTaxonomyControllerListTags();
  const tagOptions = tagsQuery.data?.tags ?? [];

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<EmailDocumentSectionResponseDtoCategory>(
    CategoryEnum.content,
  );
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reseed local form state every time the drawer opens or the underlying
  // section refetches. Using `open` as a dep means the user can discard
  // unsaved changes by closing and re-opening.
  useEffect(() => {
    if (!open) return;
    const s = sectionQuery.data;
    if (!s) return;
    setName(s.name ?? '');
    setDescription(s.description ?? '');
    setCategory(s.category);
    setTagIds((s.tags ?? []).map((tag) => tag.id));
    setIsActive(s.isActive ?? true);
    setError(null);
  }, [open, sectionQuery.data]);

  const updateMutation = useEmailDocumentSectionsControllerUpdate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (q) =>
            typeof q.queryKey[0] === 'string' &&
            q.queryKey[0].includes('email-document-sections'),
        });
        onClose();
      },
      onError: (err: any) => {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            t('sectionSettingsDrawer.failedToSave', 'Failed to save section'),
        );
      },
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      setError(t('sectionSettingsDrawer.nameRequired', 'Name is required.'));
      return;
    }
    setError(null);
    updateMutation.mutate({
      orgId,
      clientId,
      id: sectionId,
      data: {
        name: name.trim(),
        description: description.trim() || undefined,
        category,
        tagIds,
        isActive,
      },
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 420, p: 0 } } }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {t('sectionSettingsDrawer.title', 'Section Settings')}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
        <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.75, fontSize: '0.75rem' }}>
          {t(
            'sectionSettingsDrawer.libraryHint',
            'Name, description, and tags power search and AI suggestions across the library. Be specific — "Three-column footer with social icons" beats "footer".',
          )}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={1.75}>
          <TextField
            label={t('sectionSettingsDrawer.nameLabel', 'Name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            required
            helperText={t(
              'sectionSettingsDrawer.nameHelper',
              "Concrete and descriptive; this is what you'll see when browsing.",
            )}
          />
          <TextField
            label={t('sectionSettingsDrawer.descriptionLabel', 'Description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            helperText={t(
              'sectionSettingsDrawer.descriptionHelper',
              'What the section is, when to reach for it.',
            )}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>{t('sectionSettingsDrawer.categoryLabel', 'Category')}</InputLabel>
            <Select
              label={t('sectionSettingsDrawer.categoryLabel', 'Category')}
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as EmailDocumentSectionResponseDtoCategory)
              }
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            size="small"
            options={tagOptions}
            value={tagOptions.filter((tagOpt) => tagIds.includes(tagOpt.id))}
            onChange={(_, next) => setTagIds(next.map((tagOpt) => tagOpt.id))}
            getOptionLabel={(tagOpt) => tagOpt.displayName}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    {...tagProps}
                    label={option.displayName}
                    size="small"
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('sectionSettingsDrawer.tagsLabel', 'Tags')}
                helperText={t(
                  'sectionSettingsDrawer.tagsHelper',
                  'Compositional tags: layout, content shape, accents.',
                )}
              />
            )}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label={t('sectionSettingsDrawer.activeLabel', 'Active')}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button onClick={onClose} disabled={updateMutation.isPending}>
          {t('common:cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? t('common:saving') : t('common:save')}
        </Button>
      </Box>
    </Drawer>
  );
}
